import AuthPrimaryButton from "./AuthPrimaryButton";
import AuthTextField from "./AuthTextField";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useEnableTotp } from "../../application/hooks/useEnableTotp";
import { useSession } from "../../application/hooks/useSession";
import {
  AUTH_FLOW_PURPOSE_KEY,
  AUTH_FLOW_PURPOSE_RESET_TOTP,
} from "../../domain/constants/authFlowConstants";
import {
  OTP_PREFERENCE_TOTP,
  PENDING_OTP_PREFERENCE_KEY,
} from "../../domain/constants/otpConstants";

interface TotpFormProps {
  verifyAction: "twofaEnabled" | "loginSuccessful";
}

const TotpForm = ({ verifyAction }: TotpFormProps) => {
  const { t } = useTranslation("auth");
  const { verifyTotpSetup, verifyTotp, loading } = useEnableTotp();
  const { getUserEmail } = useSession();
  const navigate = useNavigate();

  const [totp, setTotp] = React.useState("");
  const [error, setError] = React.useState("");

  const validateTotp = () => {
    if (!totp.trim()) {
      setError(t("totpForm.codeRequired"));
      return false;
    }

    if (!/^\d+$/.test(totp)) {
      setError(t("totpForm.digitsOnly"));
      return false;
    }

    if (totp.length !== 6) {
      setError(t("totpForm.sixDigits"));
      return false;
    }

    setError("");
    return true;
  };

  const handleTotpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Allow only numeric input
    if (/^\d*$/.test(value)) {
      setTotp(value);

      if (error) {
        setError("");
      }
    }
  };

  const handleVerify = async () => {
    const userEmail = getUserEmail() || "";

    if (!validateTotp()) {
      return;
    }

    try {
      if (verifyAction === "twofaEnabled") {
        const isReconfigure =
          sessionStorage.getItem(AUTH_FLOW_PURPOSE_KEY) ===
          AUTH_FLOW_PURPOSE_RESET_TOTP;
        const hadPendingPreference =
          sessionStorage.getItem(PENDING_OTP_PREFERENCE_KEY) ===
          OTP_PREFERENCE_TOTP;

        const response = await verifyTotpSetup(totp);
        if (!response?.success) {
          setError(t("totpForm.invalidCode"));
          return;
        }

        if (isReconfigure || hadPendingPreference) {
          navigate("/settings");
        } else {
          navigate("/twofa-success", {
            state: { otpPreference: OTP_PREFERENCE_TOTP },
          });
        }
      } else {
        await verifyTotp(userEmail, totp, "LOGIN");
        navigate("/patients");
      }
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === "object" &&
        "response" in err &&
        (err as { response?: { data?: { message?: string } } }).response?.data
          ?.message;

      setError(
        message ||
          (err instanceof Error ? err.message : undefined) ||
          t("totpForm.invalidCode"),
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await handleVerify();
  };

  return (
    <form className="flex w-full flex-col gap-6" onSubmit={handleSubmit}>
      {" "}
      <AuthTextField
        name="totp"
        label={t("totpForm.label")}
        type="text"
        placeholder={t("totpForm.placeholder")}
        value={totp}
        onChange={handleTotpChange}
        error={!!error}
        helperText={error}
        inputProps={{
          maxLength: 6,
          inputMode: "numeric",
          pattern: "[0-9]*",
        }}
        className="w-full"
      />
      <AuthPrimaryButton type="submit" disabled={!totp} loading={loading}>
        {t("totpForm.verify")}
      </AuthPrimaryButton>
    </form>
  );
};

export default TotpForm;
